import React, { useCallback } from 'react';
import { Box, Button, Collapse, Divider, Stack, Typography } from '@mui/material';
import { ChevronRight, ExpandMore } from '@mui/icons-material';

const normalizeCollection = collectionOrWrapped => {
  if (Array.isArray(collectionOrWrapped)) {
    return collectionOrWrapped;
  }

  if (Array.isArray(collectionOrWrapped?.items)) {
    return collectionOrWrapped.items;
  }

  return [];
};

const DealerPortalMenuFilterSidebar = ({
  showFiltersForSection,
  selectedLeafFilterIds,
  clearSelectedLeafFilters,
  activeFilterChips,
  isFiltersLoading,
  visibleFilterTree,
  expandedFilterNodeIds,
  onToggleFilterNode,
  toggleLeafFilter,
  getFilterLabel,
  titleLabel,
  clearAllLabel,
  selectedLabel,
  loadingLabel,
  noneAvailableLabel,
}) => {
  const renderFilterNodes = useCallback(
    (nodes, depth = 0) =>
      nodes.map((node, nodeIndex) => {
        const children = normalizeCollection(node?.children);
        const nodeId = node?.id;
        const isLeaf = children.length === 0;
        const isExpanded = expandedFilterNodeIds.includes(nodeId);
        const isSelected = selectedLeafFilterIds.includes(nodeId);
        const label = getFilterLabel(node);

        if (isLeaf) {
          return (
            <Box key={nodeId} sx={{ pl: depth * 1.2 + 0.5, py: 0.25 }}>
              <Button
                size="small"
                onClick={() => toggleLeafFilter(nodeId)}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  width: '100%',
                  px: 0.5,
                  color: 'text.primary',
                  fontWeight: isSelected ? 600 : 400,
                }}>
                <Box
                  component="span"
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: '1.5px solid',
                    borderColor: isSelected ? 'primary.main' : 'text.secondary',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1,
                    flexShrink: 0,
                  }}>
                  {isSelected ? (
                    <Box
                      component="span"
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                      }}
                    />
                  ) : null}
                </Box>
                <Typography variant="body2" sx={{ textAlign: 'left' }}>
                  {label}
                </Typography>
              </Button>
            </Box>
          );
        }

        return (
          <Box key={nodeId}>
            <Button
              size="small"
              onClick={() => onToggleFilterNode(nodeId)}
              sx={{
                pl: depth > 0 ? depth * 1.2 + 0.25 : 0,
                py: depth === 0 ? 1.1 : 0.75,
                width: '100%',
                textTransform: 'none',
                justifyContent: 'space-between',
                color: 'text.primary',
                borderRadius: 0,
              }}>
              <Typography
                variant={depth === 0 ? 'subtitle1' : 'body2'}
                sx={{ fontWeight: depth === 0 ? 700 : 600, textAlign: 'left' }}>
                {label}
              </Typography>
              <Box component="span" sx={{ display: 'inline-flex', minWidth: 20, justifyContent: 'center' }}>
                {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
              </Box>
            </Button>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ pb: depth === 0 ? 0.5 : 0 }}>{renderFilterNodes(children, depth + 1)}</Box>
            </Collapse>
            {depth === 0 && nodeIndex < nodes.length - 1 ? <Divider sx={{ my: 0.5 }} /> : null}
          </Box>
        );
      }),
    [expandedFilterNodeIds, getFilterLabel, onToggleFilterNode, selectedLeafFilterIds, toggleLeafFilter],
  );

  if (!showFiltersForSection) {
    return null;
  }

  return (
    <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, mt: { xs: 0, md: 6 } }}>
      <Stack spacing={1.25}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {titleLabel}
          </Typography>
          {selectedLeafFilterIds.length > 0 ? (
            <Button size="small" onClick={clearSelectedLeafFilters}>
              {clearAllLabel}
            </Button>
          ) : null}
        </Box>
        <Divider />

        {activeFilterChips.length > 0 ? (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
              {selectedLabel}
            </Typography>
            <Stack spacing={0.5}>
              {activeFilterChips.map(filter => (
                <Button
                  key={filter.id}
                  size="small"
                  onClick={() => toggleLeafFilter(filter.id)}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', p: 0, minWidth: 0 }}>
                  <Box
                    component="span"
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: 'common.white',
                      fontSize: 12,
                      lineHeight: '16px',
                      textAlign: 'center',
                      mr: 1,
                    }}>
                    x
                  </Box>
                  <Typography variant="body2" sx={{ color: 'primary.main', textDecoration: 'underline', textAlign: 'left' }}>
                    {filter.label}
                  </Typography>
                </Button>
              ))}
            </Stack>
            <Divider sx={{ mt: 1 }} />
          </Box>
        ) : null}

        {isFiltersLoading ? (
          <Typography variant="body2" color="text.secondary">
            {loadingLabel}
          </Typography>
        ) : visibleFilterTree.length > 0 ? (
          <Box>{renderFilterNodes(visibleFilterTree)}</Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {noneAvailableLabel}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default DealerPortalMenuFilterSidebar;
